// components/MUIDatatable/PharmacyDebitsTable.js

import React from "react";
import { format } from "date-fns";
import CustomMUIDataTable from "components/MUIDatatable/CustomMUIDataTablev2";
import { dbGet } from "utils/DBFetchers";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";

export default function PharmacyDebitsMuiDataTable({
  pharmacyRut,
  period,         // int AAAAMM | null
  distributorId,  // int | null
  pageSize: initialPageSize = 25,
  title = "Débitos a Farmacia",
  endpointBase = "/wallet/internal/transactions",
  columnsOverride = null,
  onRowClick,
}) {
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const [tableData, setTableData] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialPageSize);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [fileUrl, setFileUrl] = React.useState(null);

  const [sortColumn, setSortColumn] = React.useState(null); // "Período" | "Fecha" | "Débito" | "Distribuidor" | "Descripción"
  const [sortDirection, setSortDirection] = React.useState(null); // "asc" | "desc"

  const COLS = [
    {
      key: "period",
      label: "Período",
      orderBy: "period",
      format: (raw) =>
        typeof raw === "number"
          ? `${String(raw).slice(0, 4)}-${String(raw).slice(4)}`
          : (raw ?? ""),
      options: {
        sort: true,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 120 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "transaction_date",
      label: "Fecha",
      orderBy: "transaction_date",
      format: (raw) => (raw ? format(new Date(raw), "dd/MM/yyyy HH:mm") : ""),
      options: {
        sort: true,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 160 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "distributor_name",
      label: "Distribuidor",
      orderBy: "distributor_name",
      format: (raw) => raw ?? "",
      options: {
        sort: true,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 220 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "pharmacy_rut",
      label: "RUT Farmacia",
      format: (raw) => raw ?? "",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 140 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "pharmacy_name_formatted",
      label: "Nombre Farmacia",
      format: (raw) => raw ?? "",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 140 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "op_code_es",
      label: "Tipo de Transacción",
      format: (raw) => raw ?? "",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 120 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "description",
      label: "Descripción",
      format: (raw) => raw ?? "",
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 280 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    {
      key: "value",
      label: "Débito",
      format: (raw) =>
        typeof raw === "number"
          ? raw.toLocaleString("es-AR", { style: "currency", currency: "ARS" })
          : (raw ?? ""),
      options: {
        sort: false,
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 140 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      },
    },
    // Auxiliares ocultas
    { key: "id", label: "Id", hidden: true, get: (row) => row.id },
    { key: "period_raw", label: "PeriodoRaw", hidden: true, get: (row) => row.period },
    { key: "date_raw", label: "FechaRaw", hidden: true, get: (row) => row.transaction_date },
    { key: "value_raw", label: "ValorRaw", hidden: true, get: (row) => row.value },
  ];

  const DEFAULT_COLUMNS = COLS.map(({ label, options, hidden }) => ({
    name: label,
    options: hidden
      ? { ...(options || {}), display: "excluded", filter: false }
      : options,
  }));
  const columns = columnsOverride ?? DEFAULT_COLUMNS;

  const ORDER_BY_MAP = React.useMemo(() => {
    const entries = COLS.filter(c => !!c.orderBy).map(c => [c.label, c.orderBy]);
    return Object.fromEntries(entries);
  }, []);

  // === Igual que la otra tabla: aceptar extra params ===
  const makeQueryParams = React.useCallback((extraParams = {}) => {
    const params = new URLSearchParams();
    if (pharmacyRut) params.append("pharmacy_rut", pharmacyRut);
    if (period) params.append("period", String(period));                  // AAAAMM
    if (distributorId) params.append("distributor_id", String(distributorId));
    params.append("page", page + 1);
    params.append("page_size", rowsPerPage);
    if (sortColumn) {
      const ob = ORDER_BY_MAP[sortColumn] || sortColumn;
      params.append("order_by", ob);
      if (sortDirection) params.append("order_dir", sortDirection);
    }
    Object.entries(extraParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null) params.append(k, String(v));
    });
    return params;
  }, [pharmacyRut, period, distributorId, page, rowsPerPage, sortColumn, sortDirection, ORDER_BY_MAP]);

  const fillTable = React.useCallback((apiPayload) => {
    const {
      page: bePage,
      page_size,
      total_pages,
      total_items,
      transactions = [],
    } = apiPayload || {};

    const nextPage = (bePage ?? 1) - 1;
    setPage((prev) => (prev !== nextPage ? nextPage : prev));
    if (typeof page_size === "number") setRowsPerPage((prev) => (prev !== page_size ? page_size : prev));
    if (typeof total_pages === "number") setTotalPages((prev) => (prev !== total_pages ? total_pages : prev));
    if (typeof total_items === "number") setCount((prev) => (prev !== total_items ? total_items : prev));

    const rows = transactions.map((row) =>
      COLS.map((col) => {
        const raw = col.get ? col.get(row) : row[col.key];
        const formatted = col.format ? col.format(raw, row) : (raw ?? "");
        return formatted;
      })
    );

    setTableData(rows);
  }, []);


  const fetchedKeyRef = React.useRef("");
  const inFlightRef = React.useRef({ key: "", running: false });

  const fetchPage = React.useCallback(() => {
    const params = makeQueryParams();
    const key = params.toString();

    // Evitar duplicados (StrictMode) y refetch idéntico ya resuelto
    if (inFlightRef.current.running && inFlightRef.current.key === key) {
      return;
    }
    if (fetchedKeyRef.current === key) {
      setIsLoading(false);
      return;
    }

    inFlightRef.current = { key, running: true };
    setIsLoading(true);

    dbGet(`${endpointBase}?${params.toString()}`)
      .then((data) => {
        fetchedKeyRef.current = key;
        setFileUrl(data?.file_url ?? null);
        fillTable(data);
      })
      .catch(() => snackbarOpen("error", "Error al cargar débitos"))
      .finally(() => {
        inFlightRef.current = { key: "", running: false };
        setIsLoading(false);
      });
  }, [makeQueryParams, endpointBase, fillTable, snackbarOpen]);

  React.useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // callbacks para CustomMUIDataTablev2
  const changePage = (newPage) => setPage(newPage);
  const changeRowsPerPage = (newRows) => {
    setRowsPerPage(newRows);
    setPage(0);
  };
  const handleSortChange = (col, dir) => {
    setSortColumn(col);
    setSortDirection(dir);
  };

  // === Descarga, alineado con la otra tabla ===
  const handleDownload = () => {
    if (isDownloading) return false;

    // Si ya tenemos URL del backend, abrir directo
    if (fileUrl) {
      try {
        const a = document.createElement("a");
        a.href = fileUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        requestAnimationFrame(() => {
          a.click();
          document.body.removeChild(a);
        });
      } catch {
        snackbarOpen("error", "No se pudo iniciar la descarga");
      }
      return false; // cancelar descarga por defecto de MUI
    }

    // Si no, pedirla explícitamente (manteniendo overlay de loading durante la descarga)
    setIsDownloading(true);
    (async () => {
      try {
        const params = makeQueryParams({ get_file_url: true });
        const data = await dbGet(`${endpointBase}?${params.toString()}`);
        const url = data?.file_url;
        if (!url) {
          snackbarOpen("warning", "No se recibió URL de descarga");
          return;
        }
        setFileUrl(url);
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        requestAnimationFrame(() => {
          a.click();
          document.body.removeChild(a);
        });
      } catch {
        snackbarOpen("error", "No se pudo iniciar la descarga");
      } finally {
        setIsDownloading(false);
      }
    })();

    return false; // MUY IMPORTANTE: cancelar descarga por defecto de MUIDataTable
  };

  return (
    <>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <CustomMUIDataTable
        title={title}
        data={tableData}
        columns={columns}
        count={count}
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        changePage={changePage}
        changeRowsPerPage={changeRowsPerPage}
        options={{
          pagination: true,
          isLoading: isLoading || isDownloading,
          filter: false,
          search: false,
          onColumnSortChange: handleSortChange,
          onDownload: handleDownload,
          onRowClick: onRowClick
            ? (rowData /* , rowMeta */) => onRowClick(rowData)
            : undefined,
        }}
      />
    </>
  );
}
